// Per-tab memory of the map view (iOS keeps the map's filter, colorize override and layers in MapSession.shared for
// the app's lifetime). sessionStorage: one browser tab's session, never shared between viewers or devices; keyed by
// company so ids from one company are never applied to another. Every access is guarded: storage can be missing or
// throw (private windows, blocked site data), and the map then simply starts unfiltered.

export interface MapLayerToggles {
  territories: boolean;
  reps: boolean;
}

export const DEFAULT_LAYERS: Readonly<MapLayerToggles> = Object.freeze({ territories: true, reps: true });

const VIEW_KEY = 'gt:map:view:';
const LAYERS_KEY = 'gt:map:layers';

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The last map query for this company in this tab ("" or null when none). */
export function loadSavedView(companyId: string): string | null {
  try {
    const v = storage()?.getItem(VIEW_KEY + companyId.toLowerCase()) ?? null;
    return v === '' ? null : v;
  } catch {
    return null;
  }
}

/** Remembers the map query; "" clears it (after Reset, the rail opens the unfiltered map). */
export function saveView(companyId: string, query: string): void {
  try {
    storage()?.setItem(VIEW_KEY + companyId.toLowerCase(), query);
  } catch {
    // Storage full or blocked: the view just is not remembered.
  }
}

/** Layer toggles parsed defensively (unknown or malformed values fall back to on). */
export function parseLayers(raw: string | null | undefined): MapLayerToggles {
  if (!raw) return { ...DEFAULT_LAYERS };
  try {
    const v = JSON.parse(raw) as Partial<Record<keyof MapLayerToggles, unknown>>;
    return {
      territories: typeof v.territories === 'boolean' ? v.territories : DEFAULT_LAYERS.territories,
      reps: typeof v.reps === 'boolean' ? v.reps : DEFAULT_LAYERS.reps,
    };
  } catch {
    return { ...DEFAULT_LAYERS };
  }
}

export function loadLayers(): MapLayerToggles {
  try {
    return parseLayers(storage()?.getItem(LAYERS_KEY));
  } catch {
    return { ...DEFAULT_LAYERS };
  }
}

export function saveLayers(layers: MapLayerToggles): void {
  try {
    storage()?.setItem(LAYERS_KEY, JSON.stringify(layers));
  } catch {
    // Not remembered; the toggles still work for this visit.
  }
}
